import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import ErrorBoundary from '@/app/ErrorBoundary';
import PageLoading from '@/components/layouts/PageLoading';
import App from '@/app/App';
import Head from '@/app/Head';

// const LoginCallback = lazy(() => import('@/app/login-callback'));
import LoginCallback from '@/app/login-callback';
// const RootPage = lazy(() => import('@/app'));
import RootPage from '@/app';
const Home = lazy(() => import('@/app/home'));
// import Home from '@/app/home';
const UsersProfileSettings = lazy(() => import('@/app/me/settings/profile-settings'));
const AccountTerminate = lazy(() => import('@/app/me/settings/account-terminate'));
const Tickets = lazy(() => import('@/app/me/tickets'));
const TicketPayments = lazy(() => import('@/app/me/tickets/payments'));
const TicketPurchaseCompleted = lazy(() => import('@/app/me/tickets/purchase/completed'));
const Notes = lazy(() => import('@/app/notes'));
// import Notes from '@/app/notes';
const NoteDetail = lazy(() => import('@/app/notes/[footballlogid]'));
const NotesCalendar = lazy(() => import('@/app/notes/calendar/[yearmonth]'));
// import NotesCalendar from '@/app/notes/calendar/[yearmonth]';
const Goals = lazy(() => import('@/app/goals'));
// import Goals from '@/app/goals';
const Supporters = lazy(() => import('@/app/supporters'));
const SupportersFeed = lazy(() => import('@/app/supporters/feeds'));
const FootballerDetail = lazy(() => import('@/app/footballers/[userid]'));
const FootballersNoteDetail = lazy(() => import('@/app/footballers/[userid]/notes/[footballlogid]'));
const Videos = lazy(() => import('@/app/videos'));
const VideoDetail = lazy(() => import('@/app/videos/[videoid]'));
const Notifications = lazy(() => import('@/app/notifications'));
const TeamHome = lazy(() => import('@/app/teams/[teamuuid]'));
const TeamNotes = lazy(() => import('@/app/teams/[teamuuid]/notes'));
const TeamMembersGoals = lazy(() => import('@/app/teams/[teamuuid]/goals'));
const TeamMembers = lazy(() => import('@/app/teams/[teamuuid]/members'));
const TeamMemberDetail = lazy(() => import('@/app/teams/[teamuuid]/members/[userid]'));
const TeamMembersNoteDetail = lazy(() => import('@/app/teams/[teamuuid]/members/[userid]/notes/[footballlogid]'));
const TeamMembersSettings = lazy(() => import('@/app/teams/[teamuuid]/members/settings'));
const TeamSettingsForOwner = lazy(() => import('@/app/teams/[teamuuid]/settings/owner-settings'));
const TeamSettingsForMember = lazy(() => import('@/app/teams/[teamuuid]/settings/member-settings'));
const TeamLeave = lazy(() => import('@/app/teams/[teamuuid]/settings/member-settings/team-leave'));

// HACK: ローディング画面を表示させるためのHACK
// const simulateDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// // 遅延を追加してコンポーネントを読み込む
// const Goals = lazy(() =>
//   simulateDelay(2000).then(() => import('@/app/goals')) // 2秒遅延
// );

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    // NOTE: ルートにもErrorBoundaryを追加しないと4XXエラー等がキャッチできないので、追加。
    errorElement: <ErrorBoundary />,
    children: [
      {
        // アプリケーション全体で共通のレイアウト (AppLayout) を維持しつつ、
        // その内部 (App の Outlet) でエラーが発生した場合に ErrorBoundary を表示するための構造。
        // これにより、/home など全てのページで共通のエラーハンドリングが可能になる。
        path: '/',
        element: <Outlet />, // この Outlet が子ルートまたはエラー要素をレンダリングする。
        errorElement: <ErrorBoundary />, // このルートのどの子でエラーが発生しても、ErrorBoundary がこの Outlet 内にレンダリングされる。
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoading />}>
                <RootPage />
              </Suspense>
            )
          },
          {
            path: 'login-callback',
            element: (
              <Suspense fallback={<PageLoading />}>
                <Head title="login">
                  <LoginCallback />
                </Head>
              </Suspense>
            )
          },
          {
            path: 'home',
            element: (
              <Suspense fallback={<PageLoading />}>
                <Head title="Home">
                  <Home />
                </Head>
              </Suspense>
            )
          },
          {
            path: 'notes',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サッカーノート">
                      <Notes />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: ':footballlogid',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サッカーノート">
                      <NoteDetail />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'calendar/:yearmonth',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サッカーノート">
                      <NotesCalendar />
                    </Head>
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'goals',
            element: (
              <Suspense fallback={<PageLoading />}>
                <Head title="目標・課題">
                  <Goals />
                </Head>
              </Suspense>
            )
          },
          {
            path: 'supporters',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サポーター">
                      <Supporters />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'feeds',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サポーター">
                      <SupportersFeed />
                    </Head>
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'footballers/:userid',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サポーター">
                      <FootballerDetail />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'notes/:footballlogid',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サポーター">
                      <FootballersNoteDetail />
                    </Head>
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'videos',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サッカー動画">
                      <Videos />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: ':videoid',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="サッカー動画">
                      <VideoDetail />
                    </Head>
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'notifications',
            element: (
              <Suspense fallback={<PageLoading />}>
                <Head title="通知">
                  <Notifications />
                </Head>
              </Suspense>
            )
          },
          {
            path: 'me',
            children: [
              {
                index: true,
                element: <Navigate to="/home" replace />
              },
              {
                path: 'settings',
                children: [
                  {
                    index: true,
                    element: <Navigate to="/home" replace />
                  },
                  {
                    path: 'profile-settings',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="設定">
                          <UsersProfileSettings />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: 'account-terminate',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="退会手続き">
                          <AccountTerminate />
                        </Head>
                      </Suspense>
                    )
                  }
                ]
              },
              {
                path: 'tickets',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="チケット購入">
                          <Tickets />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: 'payments',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="チケット購入履歴">
                          <TicketPayments />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: 'purchase/completed',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="チケット購入完了">
                          <TicketPurchaseCompleted />
                        </Head>
                      </Suspense>
                    )
                  }
                ]
              }
            ]
          },
          {
            path: 'teams/:teamuuid',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="Home - footballLOG TEAM">
                      <TeamHome />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'notes',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="チームノート - footballLOG TEAM">
                      <TeamNotes />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'goals',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <Head title="チームノート - footballLOG TEAM">
                      <TeamMembersGoals />
                    </Head>
                  </Suspense>
                )
              },
              {
                path: 'members',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="所属メンバー - footballLOG TEAM">
                          <TeamMembers />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: 'settings',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="所属メンバー設定 - footballLOG TEAM">
                          <TeamMembersSettings />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: ':userid',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<PageLoading />}>
                            <Head title="所属メンバー - footballLOG TEAM">
                              <TeamMemberDetail />
                            </Head>
                          </Suspense>
                        )
                      },
                      {
                        path: 'notes/:footballlogid',
                        element: (
                          <Suspense fallback={<PageLoading />}>
                            <Head title="所属メンバー - footballLOG TEAM">
                              <TeamMembersNoteDetail />
                            </Head>
                          </Suspense>
                        )
                      }
                    ]
                  }
                ]
              },
              {
                path: 'settings',
                children: [
                  {
                    index: true,
                    element: <Navigate to="/home" replace />
                  },
                  {
                    path: 'owner-settings',
                    element: (
                      <Suspense fallback={<PageLoading />}>
                        <Head title="チーム設定 - footballLOG TEAM">
                          <TeamSettingsForOwner />
                        </Head>
                      </Suspense>
                    )
                  },
                  {
                    path: 'member-settings',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<PageLoading />}>
                            <Head title="チーム設定 - footballLOG TEAM">
                              <TeamSettingsForMember />
                            </Head>
                          </Suspense>
                        )
                      },
                      {
                        path: 'team-leave',
                        element: (
                          <Suspense fallback={<PageLoading />}>
                            <Head title="チーム設定 - footballLOG TEAM">
                              <TeamLeave />
                            </Head>
                          </Suspense>
                        )
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]);
